import { computed, ref, watch } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import type { Conversation, ConversationMessage } from '@cutr/constants/order';
import type { ConversationLabel } from '@cutr/constants/conversations';
import { CONVERSATION_PROVIDERS } from '@cutr/constants/conversations';
import {
  hasIncomingFor,
  isRealConversationId,
  mergeConversationDetails,
  useConversationDetailsQuery,
  useConversationMutations,
  useInvalidateConversation,
  useInvalidateConversationList,
  useOrderConversationsQuery,
  withDirectPlaceholder,
} from '@/queries/conversations';
import { liveUpdates } from '@/modules/live-updates';
import { useOrderStore } from '@/stores/order';
import { useActiveUserStore } from '@/stores/user';
import { useNotificationStore } from '@/stores/notifications';

export const useConversationsStore = defineStore('conversations', () => {
  const orderStore = useOrderStore();
  const userStore = useActiveUserStore();
  const notifications = useNotificationStore();
  const mutations = useConversationMutations();
  const invalidateConversation = useInvalidateConversation();
  const invalidateConversationList = useInvalidateConversationList();

  const message = ref('');
  const showTimeline = ref(false);
  const isMessagesOpen = ref(false);
  const files = ref<{ filename: string }[]>([]);
  const conversationId = ref<string | null>(null);

  const isLoggedIn = computed(() => userStore.isLoggedIn);
  const orderId = computed(() => orderStore.currentOrder.id || '');
  const { rows, query: listQuery } = useOrderConversationsQuery(orderId, {
    enabled: isLoggedIn,
  });

  // Every thread is a provider round trip, so they are only worth fetching
  // while the tab that renders them is open.
  const detailIds = computed(() =>
    isMessagesOpen.value ? rows.value.map((row) => row.id) : []
  );
  const details = useConversationDetailsQuery(orderId, detailIds);

  const conversationRows = computed(() =>
    withDirectPlaceholder(rows.value, {
      isAdmin: userStore.isAdmin,
      makerId: orderStore.currentOrder.makerId,
      customerOrganisationId: orderStore.currentOrder.customerOrganisationId,
    })
  );

  const conversations = computed(() =>
    mergeConversationDetails(conversationRows.value, details.value.byId)
  );

  const activeConversation = computed(() =>
    conversations.value.find(
      (conversation) => conversation.id === conversationId.value
    )
  );

  const isLoading = computed(
    () => listQuery.isLoading.value || details.value.isLoading
  );
  const isListError = computed(() => listQuery.isError.value);

  const hasIncoming = computed(() =>
    hasIncomingFor(
      activeConversation.value,
      details.value.updatedAtById[conversationId.value ?? ''] ?? 0,
      listQuery.dataUpdatedAt.value
    )
  );

  const hasConversations = computed(() => rows.value.length > 0);
  const unreadCount = computed(
    () => rows.value.filter((row) => row.hasUnreadMessages).length
  );

  function refreshActiveConversation() {
    if (!isRealConversationId(conversationId.value)) return;
    return invalidateConversation({
      orderId: orderId.value,
      conversationId: conversationId.value,
    });
  }

  function select(id: string | null) {
    const hasChanged = id !== conversationId.value;

    conversationId.value = id;
    showTimeline.value = Boolean(id);

    if (!isRealConversationId(id)) return;

    if (hasChanged) {
      message.value = localStorage.getItem(id) || '';
      if (hasIncoming.value) refreshActiveConversation();
    } else {
      refreshActiveConversation();
    }

    markViewed(id);
  }

  function markViewed(id: string) {
    return mutations
      .markConversationViewed({ orderId: orderId.value, conversationId: id })
      .then(() => notifications.markRead({ resourceId: [id] }))
      .catch((error) => console.error(error));
  }

  watch([rows, isMessagesOpen], () => {
    if (!isMessagesOpen.value || conversationId.value) return;
    const [first] = rows.value;
    if (first) select(first.id);
  });

  function submit(form: HTMLFormElement) {
    if (!message.value || !isRealConversationId(conversationId.value)) return;

    const id = conversationId.value;
    const data = new FormData(form);
    data.append('body', message.value);
    data.set('isPrivate', form.isPrivate?.checked);

    return mutations
      .sendMessage({ orderId: orderId.value, conversationId: id, data })
      .then(() => {
        message.value = '';
        files.value = [];
        form.reset();
        localStorage.removeItem(id);
      });
  }

  function changeLabel(
    target: ConversationMessage,
    labels: ConversationLabel[]
  ) {
    if (!isRealConversationId(conversationId.value)) return;

    return mutations.setMessageLabels({
      orderId: orderId.value,
      conversationId: conversationId.value,
      providerMessageId: target.providerId,
      labels: (labels || []).filter(Boolean),
    });
  }

  async function createDirectChat(conversation?: Conversation) {
    const created = await mutations.createConversation({
      orderId: orderId.value,
      providerType: CONVERSATION_PROVIDERS.zendesk,
      makerOrganisationId: conversation?.makerOrganisationId,
      customerOrganisationId: conversation?.customerOrganisationId,
    });
    if (created) select(created.id);
  }

  async function removeDirectChat(conversation?: Conversation) {
    if (!isRealConversationId(conversation?.id)) return;

    // Clearing first keeps the detail observer from refetching a 404 between
    // the delete and the list refresh.
    conversationId.value = null;
    await mutations.deleteConversation({
      orderId: orderId.value,
      conversationId: conversation.id,
    });
  }

  function subscribeToNewMessages() {
    return liveUpdates.handleEvent<{ id: string }>(
      'new_conversation_message',
      ({ payload }) => {
        if (!rows.value.some((row) => row.id === payload.id)) return;
        invalidateConversationList(orderId.value);
      }
    );
  }

  function reset() {
    files.value = [];
    message.value = '';
    showTimeline.value = false;
    conversationId.value = null;
    isMessagesOpen.value = false;
  }

  return {
    conversationId,
    message,
    files,
    showTimeline,
    isMessagesOpen,
    rows,
    conversations,
    activeConversation,
    hasConversations,
    hasIncoming,
    isLoading,
    isListError,
    unreadCount,
    select,
    submit,
    changeLabel,
    createDirectChat,
    removeDirectChat,
    subscribeToNewMessages,
    refreshActiveConversation,
    reset,
    ...mutations,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useConversationsStore, import.meta.hot)
  );
}
